import { useTranslation } from 'common/hooks/useTranslation';
import { useNotifications } from 'modules/app/notifications';
import {
  type ExpenseAccount,
  getDefaultExpenseAccountName,
} from 'modules/bookkeep';
import { useSetDefaultExpenseAccountMutation } from 'modules/bookkeep/settings/integrations/hooks/useSetDefaultExpenseAccountMutation';
import type { DefaultExpenseAccountFormValues } from 'modules/onboarding/setup-hub/pages/Accounting/AccountingExpenseAccounts/hooks/form';
import { useAccountingCountry } from 'modules/onboarding/setup-hub/pages/Accounting/hooks/useAccountingCountry';

export const useUpdateDefaultExpenseAccount = () => {
  const accountingCountry = useAccountingCountry();
  const { dangerNotif } = useNotifications();
  const { t } = useTranslation('global');
  const [setDefaultExpenseAccount] = useSetDefaultExpenseAccountMutation();

  return async (
    oldDefaultExpenseAccount: ExpenseAccount | undefined,
    newValues: DefaultExpenseAccountFormValues,
    successCallback: () => void,
  ) => {
    const newDefaultExpenseAccountCode =
      newValues.defaultExpenseAccountCode?.trim();
    try {
      // If defaultExpenseAccountCode is set, we set the default expense account
      if (newValues.useDefaultValue && newDefaultExpenseAccountCode?.length) {
        const result = await setDefaultExpenseAccount({
          ...oldDefaultExpenseAccount,
          name:
            oldDefaultExpenseAccount?.name ||
            getDefaultExpenseAccountName(accountingCountry),
          code: newDefaultExpenseAccountCode,
          isArchived: false,
        });
        if (result.outcome === 'notSet') {
          dangerNotif(
            result.reason === 'codeAlreadyExists'
              ? t(
                  'bookkeep.integrations.settings.defaultExpenseAccountForm.codeAlreadyExistsError',
                )
              : t('misc.errors.unknownError'),
          );
          return;
        }
      }
      // If the default Expense account was not archived
      // and we are not setting any value, then we archive it (otherwise it's already archived, nothing to update)
      if (
        !newValues.useDefaultValue &&
        oldDefaultExpenseAccount &&
        !oldDefaultExpenseAccount.isArchived
      ) {
        const result = await setDefaultExpenseAccount({
          ...oldDefaultExpenseAccount,
          isArchived: true,
        });
        if (result.outcome === 'notSet') {
          dangerNotif(t('misc.errors.unknownError'));
          return;
        }
      }
      successCallback();
    } catch {
      return dangerNotif(t('misc.errors.unknownError'));
    }
  };
};
